import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import MultiSlotBookingModal from '../components/MultiSlotBookingModal';
import DatePicker from '../components/DatePicker';
import WaitlistModal from '../components/WaitlistModal';
import BarbershopOwnerPage from './BarbershopOwnerPage';
import { getNext30Days } from '../data/mock';
import { useAppContext } from '../context/AppContext';
import { getSlotsByBarbershop } from '../services/slotService';
import { getReviewsByBarbershop } from '../services/reviewService';
import { isUserInWaitlist, getUserPositionInWaitlist } from '../services/waitlistService';
import { getBarbershopProfessionals } from '../services/invitationService';
import { openWhatsApp, msgClientRequestsBooking, msgClientEntersWaitlist } from '../services/whatsappService';
import toast from 'react-hot-toast';

/**
 * Página: BarbershopPage (/pages/BarbershopPage.jsx)
 * Página de uma barbearia específica (Visão do Usuário)
 */
const BarbershopPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, barbershops, availableSlots, appointments, createAppointment, addToWaitlist, updateAppointmentStatus } = useAppContext();
  
  const selectedShopId = id;

  // Detectar modo de agendamento por conta do barbeiro (?clientView=true)
  const clientViewMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('clientView') === 'true';
  }, [location.search]);
  
  const [selectedDate, setSelectedDate] = useState(getNext30Days()[0]); // Hoje
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientSlots, setClientSlots] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average: 0, total: 0 });
  const [isInWaitlist, setIsInWaitlist] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState({ position: 0, total: 0 });
  const [professionals, setProfessionals] = useState([]);

  const shop = useMemo(() => barbershops.find(b => b.id === selectedShopId), [barbershops, selectedShopId]);

  // Helper: verificar se o usuário é owner desta barbearia (suporta formato antigo e novo)
  const isOwnerOfShop = useMemo(() => {
    if (!currentUser || currentUser.role !== 'owner' || !shop) return false;
    
    // Formato antigo: string barbershopId
    if (currentUser.barbershopId === shop.id) return true;
    
    // Formato novo: array barbershops
    if (currentUser.barbershops && currentUser.barbershops.includes(shop.id)) return true;
    
    return false;
  }, [currentUser, shop]);

  // Redirecionar usuários não logados
  useEffect(() => {
    if (!currentUser) {
      toast.error('Você precisa fazer login para acessar esta página');
      navigate('/login', { state: { from: `/barbershop/${id}` } });
    }
  }, [currentUser, navigate, id]);

  // Carregar avaliações da barbearia
  useEffect(() => {
    const loadReviews = async () => {
      if (selectedShopId) {
        const result = await getReviewsByBarbershop(selectedShopId);
        if (result.success) {
          setReviews(result.data);
          
          // Calcular média
          if (result.data.length > 0) {
            const sum = result.data.reduce((acc, review) => acc + review.rating, 0);
            const avg = sum / result.data.length;
            setReviewStats({
              average: Math.round(avg * 10) / 10,
              total: result.data.length
            });
          }
        }
      }
    };

    loadReviews();
  }, [selectedShopId]);

  // Carregar profissionais da barbearia
  useEffect(() => {
    const loadProfessionals = async () => {
      if (selectedShopId) {
        try {
          const professionalsData = await getBarbershopProfessionals(selectedShopId);
          setProfessionals(professionalsData);
        } catch (error) {
          console.error('Erro ao carregar profissionais:', error);
          // Não mostrar erro ao usuário, apenas não exibir a seção
        }
      }
    };

    loadProfessionals();
  }, [selectedShopId]);

  // REMOVIDO: Carregar owner causava erro de permissão para clientes
  // Dados do owner não são necessários na BarbershopPage

  // Carregar slots da barbearia para clientes, admins e owners no modo cliente
  useEffect(() => {
    const loadSlots = async () => {
      if (selectedShopId && (
        currentUser?.role === 'user' ||
        currentUser?.role === 'admin' ||
        (currentUser?.role === 'owner' && clientViewMode)
      )) {
        const result = await getSlotsByBarbershop(selectedShopId);
        if (result.success) {
          // Filtrar slots que já passaram (usando horário local)
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const today = `${year}-${month}-${day}`;
          const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
          
          const validSlots = result.data.filter(slot => {
            // Se a data é futura, está válido
            if (slot.date > today) return true;
            
            // Se é hoje, verificar se o horário ainda não passou
            if (slot.date === today) {
              return slot.time > currentTime;
            }
            
            // Datas passadas são inválidas
            return false;
          });
          
          setClientSlots(validSlots);
        }
      }
    };

    loadSlots();
  }, [selectedShopId, currentUser, clientViewMode]);

  // Usa availableSlots do contexto para owners (exceto modo cliente), ou clientSlots para usuários
  const slotsToUse = (currentUser?.role === 'owner' && !clientViewMode) ? availableSlots : clientSlots;

  // Filtra horários para a barbearia e data selecionadas
  const slotsForDay = useMemo(() => {
    return slotsToUse.filter(
      (slot) => slot.barbershopId === selectedShopId && slot.date === selectedDate
    ).map(slot => {
      // Verifica se há um agendamento confirmado ou pendente para este slot
      const appointment = appointments.find(
        a => a.slotId === slot.id && (a.status === 'confirmed' || a.status === 'pending')
      );
      if (appointment) {
        return { ...slot, isBooked: true, clientName: appointment.clientName };
      }
      return slot;
    }).sort((a, b) => a.time.localeCompare(b.time)); // Ordena do menor para o maior horário
  }, [slotsToUse, appointments, selectedShopId, selectedDate]);

  // Gera os próximos 7 dias para o seletor
  const weekDates = useMemo(() => {
    return getNext30Days().slice(0, 7).map(dateStr => {
      const date = new Date(dateStr + 'T12:00:00'); // Adiciona T12 para evitar problemas de fuso
      return {
        fullDate: dateStr,
        dayName: date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
        dayNum: date.getDate(),
      };
    });
  }, []);

  const handleSlotClick = (slot) => {
    if (slot.isBooked) return;
    
    // Verificar se a barbearia tem serviços cadastrados
    if (!shop.services || !Array.isArray(shop.services) || shop.services.length === 0) {
      toast.error('Esta barbearia ainda não cadastrou serviços. Não é possível fazer agendamentos no momento.');
      return;
    }
    
    setSelectedSlot(slot);
    setIsModalOpen(true);
  };

  const handleBookingSubmit = async (formData) => {
    try {
      // Extrair slots do formData (MultiSlotBookingModal passa array de slots)
      const slots = formData.slots || [selectedSlot];
      const slotIds = slots.map(s => s.id);
      
      const newAppointment = {
        service: formData.service,
        clientName: formData.clientName,
        clientPhone: formData.clientPhone,
        notes: formData.notes,
        userId: currentUser.id,
        barbershopId: selectedShopId,
        slotIds: slotIds, // Array de IDs dos slots
        date: slots[0].date,
        time: slots[0].time,
        totalDuration: formData.totalDuration || 30,
        status: 'pending',
        ...(formData.servicePrice && { servicePrice: formData.servicePrice }),
        ...(formData.serviceDuration && { serviceDuration: formData.serviceDuration }),
        // Indicador de agendamento feito pelo próprio barbeiro
        ...(clientViewMode && { bookedByOwner: true, ownerNote: 'Agendado pelo barbeiro' }),
      };
      
      // Criar o appointment (AppContext já marca os slots como ocupados)
      const result = await createAppointment(newAppointment);
      
      if (!result.success) {
        toast.error(result.error || 'Erro ao criar agendamento');
        return;
      }

      // Quando o próprio barbeiro agenda, confirmar automaticamente
      if (clientViewMode && result.id) {
        await updateAppointmentStatus(result.id, 'confirmed');
        toast.success(`Horário reservado para ${formData.clientName}! ✅`);
      } else if (!clientViewMode && shop?.phone) {
        // Cliente notifica a barbearia via WhatsApp
        openWhatsApp(shop.phone, msgClientRequestsBooking({
          barbershopName: shop.name,
          service: formData.service,
          date: slots[0].date,
          time: slots[0].time,
        }));
      }
      // O toast de sucesso para clientes já é exibido pelo AppContext
    } catch (error) {
      console.error('Erro ao criar agendamento:', error);
      toast.error('Erro ao criar agendamento. Tente novamente.');
    }
  };

  const handleWaitlistSubmit = async (data) => {
    const waitlistData = {
      barbershopId: selectedShopId,
      date: selectedDate,
      preferredTimes: data.preferredTimes
    };

    const result = await addToWaitlist(waitlistData);
    
    if (result.success) {
      setIsWaitlistModalOpen(false);
      setIsInWaitlist(true);
      // Cliente notifica a barbearia via WhatsApp
      if (shop?.phone) {
        openWhatsApp(shop.phone, msgClientEntersWaitlist({
          barbershopName: shop.name,
          service: 'A combinar',
          desiredDate: selectedDate,
          desiredTime: data.preferredTimes?.length
            ? data.preferredTimes.join(', ')
            : 'Qualquer horário',
        }));
      }
    }
  };

  // Verificar se usuário já está na fila quando mudar a data
  useEffect(() => {
    const checkWaitlist = async () => {
      if (currentUser && selectedShopId && selectedDate) {
        const inWaitlist = await isUserInWaitlist(selectedShopId, currentUser.id, selectedDate);
        setIsInWaitlist(inWaitlist);
        
        // Se está na fila, buscar posição
        if (inWaitlist) {
          const position = await getUserPositionInWaitlist(selectedShopId, currentUser.id, selectedDate);
          setWaitlistPosition(position);
        } else {
          setWaitlistPosition({ position: 0, total: 0 });
        }
      }
    };
    
    checkWaitlist();
  }, [currentUser, selectedShopId, selectedDate]);

  if (!shop) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Barbearia não encontrada.</p>
        <Button onClick={() => navigate('/')}>Voltar para Home</Button>
      </div>
    );
  }
  
  // Return early se não tiver usuário
  if (!currentUser) {
    return null;
  }
  
  // Se for o dono da barbearia (não admin) e NÃO estiver no modo de agendamento de cliente, redireciona para a página de dono
  if (isOwnerOfShop && !clientViewMode) {
    return <BarbershopOwnerPage />;
  }

  // Verificar se a barbearia tem serviços cadastrados
  const hasServices = shop.services && Array.isArray(shop.services) && shop.services.length > 0;

  return (
    <div className="bg-gray-50 min-h-screen">
      <main>        
        {/* Banner do modo de agendamento pelo barbeiro */}
        {clientViewMode && (
          <div className="bg-indigo-700 text-white px-4 py-3">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="text-2xl shrink-0">✂️</span>
                <div>
                  <p className="font-bold text-sm sm:text-base">
                    Modo Barbeiro — Agende por um cliente
                  </p>
                  <p className="text-indigo-200 text-xs sm:text-sm">
                    Clique em um horário disponível e preencha os dados do cliente. O agendamento será confirmado automaticamente.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/barbershop/${selectedShopId}`)}
                className="shrink-0 bg-white text-indigo-700 font-semibold text-sm px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors w-full sm:w-auto text-center"
              >
                ← Voltar ao Gerenciamento
              </button>
            </div>
          </div>
        )}

        {/* Banner */}
        <div className="h-64 md:h-80 relative">
          <img src={shop.bannerUrl} alt={shop.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          <div className="absolute bottom-0 left-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="p-4 pb-12 sm:pb-14 md:p-8 md:pb-16">
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold text-white shadow-lg drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{shop.name}</h1>
            </div>
          </div>
        </div>

        {/* Conteúdo da Página */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-[-30px] sm:mt-[-40px]">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            
            {/* Coluna de Horários (Principal) */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-xl p-6 md:p-8 z-10 relative">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Agendar Horário</h2>
                
                {/* Seletor de Data */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">Selecione um dia</h3>
                    <button
                      onClick={() => setIsDatePickerOpen(true)}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center gap-2"
                    >
                      {icons.calendar({ className: 'h-4 w-4' })}
                      Ver Calendário
                    </button>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                    {weekDates.map(date => (
                      <button
                        key={date.fullDate}
                        onClick={() => setSelectedDate(date.fullDate)}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                          selectedDate === date.fullDate
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-400'
                        }`}
                      >
                        <span className="text-xs font-bold uppercase">{date.dayName}</span>
                        <span className="text-xl font-extrabold mt-1">{date.dayNum}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lista de Horários */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Horários disponíveis para {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
                  </h3>
                  
                  {/* Aviso se não houver serviços cadastrados */}
                  {!hasServices && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          {icons.alert({ className: 'h-5 w-5 text-orange-600' })}
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-orange-800">
                            Serviços não cadastrados
                          </h3>
                          <p className="mt-1 text-sm text-orange-700">
                            Esta barbearia ainda não cadastrou seus serviços. Os agendamentos estão temporariamente indisponíveis.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="max-h-[400px] overflow-y-auto pr-2">
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                      {slotsForDay.length > 0 ? (
                        slotsForDay.map(slot => (
                          <button
                            key={slot.id}
                            onClick={() => handleSlotClick(slot)}
                            disabled={slot.isBooked || !hasServices}
                            className={`p-3 rounded-lg text-center font-semibold transition-all ${
                              slot.isBooked
                                ? 'bg-gray-100 text-gray-400 line-through cursor-not-allowed'
                                : !hasServices
                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                : 'bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'
                            }`}
                          >
                            {slot.time}
                          </button>
                        ))
                      ) : (
                        <div className="col-span-full">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                            <p className="text-gray-700 mb-4">
                              ⏰ Nenhum horário disponível para <strong>{new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong>
                            </p>
                            <p className="text-sm text-gray-600 mb-4">
                              Quer ser notificado quando um horário for liberado?
                            </p>
                            {isInWaitlist && waitlistPosition.position > 0 && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                <p className="text-blue-800 font-semibold">
                                  📍 Você está na posição <span className="text-2xl font-bold text-blue-600">#{waitlistPosition.position}</span> de {waitlistPosition.total}
                                </p>
                                <p className="text-sm text-blue-600 mt-1">
                                  Será notificado por ordem de chegada quando houver vagas
                                </p>
                              </div>
                            )}
                            <Button
                              variant="primary"
                              onClick={() => setIsWaitlistModalOpen(true)}
                              disabled={isInWaitlist}
                            >
                              {isInWaitlist ? (
                                <>
                                  {icons.check({ className: 'h-5 w-5 mr-2' })}
                                  Você está na fila
                                </>
                              ) : (
                                <>
                                  🔔 Entrar na Fila de Espera
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Coluna de Informações (Sidebar) */}
            <div className="lg:col-span-1 mt-8 lg:mt-0">
               <div className="bg-white rounded-xl shadow-xl p-6 sticky top-24 space-y-6">
                 <div>
                   <h3 className="text-xl font-bold text-gray-900 mb-4">Sobre a Barbearia</h3>
                   <p className="text-gray-600 mb-4">{shop.description}</p>
                   <div className="flex items-center text-gray-700 mb-2">
                     {icons.mapPin({ className: 'h-5 w-5 mr-2 text-indigo-600' })}
                     <span>{shop.city}</span>
                   </div>
                   
                   {/* Avaliações */}
                   <div className="flex items-center text-gray-700 mt-4 p-3 bg-yellow-50 rounded-lg">
                     {icons.star({ className: 'h-6 w-6 mr-2 text-yellow-500 fill-current' })}
                     <div>
                       <span className="text-2xl font-bold text-gray-900">{reviewStats.average || 0}</span>
                       <span className="text-sm text-gray-600 ml-2">({reviewStats.total} {reviewStats.total === 1 ? 'avaliação' : 'avaliações'})</span>
                     </div>
                   </div>
                 </div>

                 {/* Contato */}
                 {(shop.address || shop.phone) && (
                   <div className="border-t pt-6">
                     <h3 className="text-lg font-bold text-gray-900 mb-4">Contato</h3>
                     
                     {shop.address && (
                       <div className="flex items-start text-gray-700 mb-3">
                         {icons.mapPin({ className: 'h-5 w-5 mr-2 text-indigo-600 mt-0.5 flex-shrink-0' })}
                         <span className="text-sm">{shop.address}</span>
                       </div>
                     )}
                     
                     {shop.phone && (
                       <div className="flex items-center text-gray-700">
                         {icons.phone({ className: 'h-5 w-5 mr-2 text-indigo-600' })}
                         <a href={`tel:${shop.phone.replace(/\D/g, '')}`} className="text-sm hover:text-indigo-600 transition-colors">
                           {shop.phone}
                         </a>
                       </div>
                     )}
                   </div>
                 )}

                 {/* Seção "Sobre o Proprietário" removida - causava erro de permissão para clientes */}
               </div>
            </div>

          </div>

          {/* Seção de Serviços e Preços */}
          {shop.services && shop.services.length > 0 && (
            <div className="mt-12 bg-white rounded-xl shadow-xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Serviços e Preços</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shop.services.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors">
                    <div>
                      <h4 className="font-semibold text-gray-900">{service.name}</h4>
                      <p className="text-sm text-gray-500">{service.duration}</p>
                    </div>
                    <span className="text-xl font-bold text-indigo-600">
                      R$ {typeof service.price === 'number' ? service.price.toFixed(2) : service.price}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Seção de Profissionais */}
          {professionals.length > 0 && (
            <div className="mt-12 bg-white rounded-xl shadow-xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Nossos Profissionais</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {professionals.map((professional) => (
                  <div 
                    key={professional.id} 
                    className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-all cursor-pointer hover:border-indigo-300"
                    onClick={() => navigate(`/owner/${professional.id}`)}
                  >
                    <div className="flex items-center mb-3">
                      {professional.photoURL ? (
                        <img 
                          src={professional.photoURL} 
                          alt={professional.name}
                          className="w-12 h-12 rounded-full mr-3 object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg mr-3">
                          {professional.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900">{professional.name}</h4>
                        <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full mt-1">
                          {professional.specialty}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-semibold">Experiência:</span> {professional.experience}
                    </p>
                    <p className="text-xs text-indigo-600 flex items-center justify-end font-semibold">
                      Ver perfil completo →
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Seção de Avaliações */}
          {reviews.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Avaliações dos Clientes</h2>
              <div className="space-y-4">
                {reviews.slice(0, 10).map(review => (
                  <div key={review.id} className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-gray-900">{review.userName}</p>
                        <div className="flex items-center mt-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star}>
                              {icons.star({
                                className: `h-4 w-4 ${
                                  star <= review.rating
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`
                              })}
                            </span>
                          ))}
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">
                        {review.createdAt?.toDate ? 
                          review.createdAt.toDate().toLocaleDateString('pt-BR') : 
                          'Recente'}
                      </span>
                    </div>
                    
                    {review.comment && (
                      <p className="text-gray-700 mb-3">{review.comment}</p>
                    )}
                    
                    {review.ownerReply && (
                      <div className="mt-4 pl-4 border-l-2 border-indigo-200 bg-indigo-50 p-3 rounded">
                        <p className="text-sm font-semibold text-indigo-900 mb-1">Resposta da barbearia:</p>
                        <p className="text-sm text-gray-700">{review.ownerReply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {reviews.length > 10 && (
                <p className="text-center text-gray-500 mt-4">
                  Exibindo 10 de {reviews.length} avaliações
                </p>
              )}
            </div>
          )}
        </div>
      </main>

      <MultiSlotBookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialSlot={selectedSlot}
        availableSlots={clientSlots.length > 0 ? clientSlots : slotsToUse}
        barbershopName={shop.name}
        barbershopServices={shop.services || []}
        onSubmit={handleBookingSubmit}
        ownerBookingMode={clientViewMode}
      />

      {/* DatePicker Modal */}
      {isDatePickerOpen && (
        <DatePicker
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          onClose={() => setIsDatePickerOpen(false)}
        />
      )}

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
        onSubmit={handleWaitlistSubmit}
        barbershopName={shop.name}
        selectedDate={selectedDate}
      />
    </div>
  );
};

export default BarbershopPage;
